// ITEM 12 (her `10`) — **THE MAGIC ROOM FILE CABINET: WHAT IS IN THE DRAWERS, WHAT ORDER THEY ARE
// IN, AND WHAT MAY BE DONE WITH A SEAL ONCE IT IS OPEN.** (her order, 2026-08-11)
//
// Her item, in her words: *"Greater Keys ordered by PLANET then NUMBER (4th and 6th Saturn absent,
// 7th present). The Sixth Book's seven complete. The Seventh Book honestly awaiting her plates.
// Seals of God: download and save-to-journal ONLY. Per-seal actions: download print quality, save to
// journal, send to cauldron. Verbatim laws. Ban laws at EVERY layer. Styled as room furniture."*
//
// **IT IS A `.js` LAW AND NOT A FUNCTION IN THE TSX, FOR THE REASON THIS HOUSE HAS PAID FOR REPEATEDLY
// (R8/R95/MR-MOB):** Node refuses a `.tsx` import outright, so an ordering law written inside the
// component is an ordering law nothing runs — and the ORDER of forty-two pentacles is exactly the
// half that rots invisibly, because a cabinet in the wrong order renders perfectly. Out here
// `scripts/file-cabinet.mjs` RUNS every clause below, and the SERVER imports the same file, so the
// route that fills the drawers and the screen that draws them cannot hold two opinions (R28/R29).
//
// >>> **THE BAN ROSTER MOVED HERE, AND IT IS THE ONE STRUCTURAL EDIT THIS ROUND MAKES TO A STANDING
// LAW.** <<< B1's two permanently-banned Saturn pentacles were declared in `server/db/seals.js` TWICE
// — once as a JS `Set` and once, separately, as a hand-typed SQL fragment (`ordinal = ANY('{4,6}')`)
// — which is two spellings of one fact on the one law in this house that may never drift (R28/R29).
// The roster is declared ONCE here; the store IMPORTS it and DERIVES its SQL from it, and the client
// imports it too, so her *"ban laws at EVERY layer"* clause is a property of one declaration rather
// than of four places agreeing. **The ban itself is untouched: the 4th and 6th Pentacles of Saturn,
// and the 7th APPEARS (her ruling 2026-07-25 lifted its ban and re-purposed it).**
//
// >>> **AND DERIVING THAT SQL IS WHAT FOUND A LIVE DEFECT THREE-VALUED LOGIC HAD BEEN HIDING FOR AS
// LONG AS THE SEALS OF GOD HAVE EXISTED.** <<< The hand-typed fragment reads
// `NOT (lower(planet) = 'saturn' AND ordinal = ANY('{4,6}'))`, and the three divine-name seals are
// stored with **`planet: null, ordinal: null`** (`server/ingest/seals.mjs` writes them that way on
// purpose — they are not planetary). In SQL `NULL AND NULL` is NULL and `NOT NULL` is NULL, and a
// `WHERE` clause that answers NULL **drops the row**. Measured against the live table: 47 rows in,
// **`listSeals()` returned 42** — the two banned, and *the three Seals of God*, all silently absent.
//
// **THE COST OF THAT WAS NOT HYPOTHETICAL AND IS NAMED RATHER THAN QUIETLY REPAIRED (R19).**
// `buildLalaSystem` composes `SEALS YOU CAN HAND OVER … Also the divine-name seals: ${divine}` by
// filtering `listSeals()` for `category === "divine-name"` — **so that sentence has been ending in
// nothing, on every turn, for every member**, and `/api/seals` has never listed them either. Nobody
// was ever told, because a list that is short is not a list that is wrong (R8-4's silent direction).
// The key comparison below is NULL-SAFE by construction — it is `pentacleKey` in SQL, the same
// concatenation, so the two dialects are one law — and the count goes 42 -> **45**, which is her 42
// pentacles plus her 3 Seals of God, with the two banned still gone.
//
// This module imports NOTHING. That is the entry requirement for a law a `scripts/` gate can load
// and RUN, and it is what lets the FAST tier drive every branch below with no database, no network
// and no browser.

// ─────────────────────────────────────────────────────────────────────────────
// B1 — THE PERMANENTLY BANNED PENTACLES. ONE DECLARATION, EVERY LAYER.
//
// The 4th Pentacle of Saturn ("serveth for ruin, destruction, and death") and the 6th (works harm
// "against whom it is pronounced") are never delivered on ANY path — the store's SQL, the store's JS
// filter, the by-slug read, the image bytes, this cabinet's route, and this cabinet's screen. Keyed
// by planet+ordinal, the pair that uniquely identifies a pentacle, so a slug or casing variant can
// never slip one through.
export const BANNED_PENTACLE_KEYS = Object.freeze(["saturn:4", "saturn:6"]);

// **THE KEY, AND ITS NULL BEHAVIOUR IS THE POINT.** A row with no planet and no ordinal — which is
// exactly what a Seal of God is — answers `":"`, a key no ban can ever match. The SQL half in
// `server/db/seals.js` is `coalesce(lower(planet), '') || ':' || coalesce(ordinal::text, '')`, which
// is this function character for character, so the JS filter and the WHERE clause agree on every
// row including the null ones (R28/R29 — and the old pair did NOT, which is the finding above).
export function pentacleKey(planet, ordinal) {
  const p = String(planet === null || planet === undefined ? "" : planet).trim().toLowerCase();
  const blank = ordinal === null || ordinal === undefined || ordinal === "" || Number.isNaN(Number(ordinal));
  return `${p}:${blank ? "" : String(Number(ordinal))}`;
}

export function isBannedPentacleKey(planet, ordinal) {
  return BANNED_PENTACLE_KEYS.includes(pentacleKey(planet, ordinal));
}

// The client-side layer of her *"ban laws at EVERY layer"* clause. The route already cannot send one;
// this is the belt, and it is a FILTER rather than a check so a banned row cannot reach a render at
// all. Non-mutating: sorting or splicing a caller's array is a defect with no symptom until the
// second caller.
export function withoutBannedPentacles(items) {
  return (Array.isArray(items) ? items : []).filter((s) => s && !isBannedPentacleKey(s.planet, s.ordinal));
}

// ─────────────────────────────────────────────────────────────────────────────
// HER ORDER: BY PLANET, THEN BY NUMBER.
//
// **THE PLANET ORDER IS THE SOURCE BOOK'S OWN AND IS NOT INVENTED HERE.** The Greater Key of Solomon
// runs Saturn, Jupiter, Mars, Sun, Venus, Mercury, Moon, and so does her own purpose sheet
// (`server/data/seal-purpose-index.json`, whose first-appearance order is exactly this list).
// `scripts/file-cabinet.mjs` holds that as a BICONDITIONAL against her sheet — every planet she has
// pentacles for is on this list, in her sheet's order, and this list names no planet she does not —
// so the declaration cannot drift away from the thing it was read off (R11/R31).
export const KEYS_PLANET_ORDER = Object.freeze(["saturn", "jupiter", "mars", "sun", "venus", "mercury", "moon"]);

// **AN UNROSTERED PLANET SORTS LAST; IT IS NEVER DROPPED (R19's direction).** A seal missing from the
// cabinet is a seal she owns and cannot find, silently. A seal at the bottom of the list is visible
// and somebody says so.
export function keysPlanetIndex(planet) {
  const i = KEYS_PLANET_ORDER.indexOf(String(planet === null || planet === undefined ? "" : planet).trim().toLowerCase());
  return i === -1 ? KEYS_PLANET_ORDER.length : i;
}

export function orderGreaterKeys(seals) {
  return [...(Array.isArray(seals) ? seals : [])].sort((a, b) => {
    const pa = keysPlanetIndex(a && a.planet), pb = keysPlanetIndex(b && b.planet);
    if (pa !== pb) return pa - pb;
    const oa = Number(a && a.ordinal) || 0, ob = Number(b && b.ordinal) || 0;
    if (oa !== ob) return oa - ob;
    // A total order, so two runs of this cabinet can never disagree about which row is first.
    return String((a && a.name) || "").localeCompare(String((b && b.name) || ""));
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// THE FOUR DRAWERS, DECLARED ONCE.
//
// `note` is what the drawer says about itself under its own name. The Seventh Book's is HER OWN
// RULING put on screen: its twelve tables carry `plate: null` — an owner-staged asset that was never
// delivered — so MRM-6 fails them closed, and her word is *"ship the seven, and the Seventh Book
// section states it awaits her plates rather than going silently missing."* An empty drawer with no
// sentence in it is the going-silently-missing this clause exists to refuse.
export const DRAWER_KEYS = "keys";
export const DRAWER_MOSES_SIXTH = "moses-sixth";
export const DRAWER_MOSES_SEVENTH = "moses-seventh";
export const DRAWER_SEALS_OF_GOD = "seals-of-god";

export const CABINET_DRAWERS = Object.freeze([
  {
    key: DRAWER_KEYS,
    label: "The Greater Keys of Solomon",
    note: "The planetary pentacles, by planet and by number, each with its own source purpose and its verse.",
  },
  {
    key: DRAWER_MOSES_SIXTH,
    label: "The Sixth Book of Moses",
    note: "The seven seals, each with the plate cut from the book and its own conjuration, word for word.",
  },
  {
    key: DRAWER_MOSES_SEVENTH,
    label: "The Seventh Book of Moses",
    note: "The twelve tables are catalogued and waiting on their plates. Until the plates are here nothing is handed over, because half a seal is not a seal.",
  },
  {
    key: DRAWER_SEALS_OF_GOD,
    label: "The Seals of God",
    note: "El Shaddai, Elohim Gibor, Elohim Tzaabaoth. A Greater Keys pentacle is placed inside the circle of the one it pairs with, and the work is done in that name.",
  },
]);

export const CABINET_DRAWER_KEYS = CABINET_DRAWERS.map((d) => d.key);

export function cabinetDrawer(key) {
  return CABINET_DRAWERS.find((d) => d.key === String(key || "")) || null;
}

// ─────────────────────────────────────────────────────────────────────────────
// WHAT MAY BE DONE WITH A SEAL, AND THE ONE CLAUSE OF HERS THAT IS A REFUSAL.
//
// >>> **A SEAL OF GOD IS NEVER SENT TO THE CAULDRON, AND THAT IS HER LAW RATHER THAN AN OVERSIGHT.**
// <<< Her item names the three actions for a seal and then names a SHORTER set for the Seals of God:
// *"download and save-to-journal ONLY."* It agrees with N11's fourth rule — a Seal of God is never
// offered on its own — and with what the Cauldron does with one: `buildCauldronWorking` marks a
// paired Seal of God so it takes its place INSIDE the pentacle's own steps (W12-1), so a Seal of God
// arriving in the pot alone is a working with a circle and nothing to put in it. The refusal is a
// property of this function, driven over every drawer, rather than a control somebody remembered not
// to render (`file-cabinet-mutations.mjs` M6 is the widening).
//
// **AND A THING WITH NO PLATE HAS NOTHING TO HAND OVER AT ALL (MRM-6, fail-closed).** The Seventh
// Book's twelve answer `[]`. Not a greyed button, not a download of the empty string: no actions.
export const CABINET_ACTIONS = Object.freeze(["download", "journal", "cauldron"]);

export function cabinetActionsFor(item) {
  if (!item) return [];
  // Belt: a banned pentacle has no actions even if one somehow reached a screen.
  if (isBannedPentacleKey(item.planet, item.ordinal)) return [];
  if (!item.imageUrl) return [];
  if (item.drawer === DRAWER_SEALS_OF_GOD) return ["download", "journal"];
  return ["download", "journal", "cauldron"];
}

export function cabinetCan(item, action) {
  return cabinetActionsFor(item).includes(String(action || ""));
}

// ─────────────────────────────────────────────────────────────────────────────
// HER VERBATIM CLAUSE, AS ARITHMETIC.
//
// Every line this cabinet writes into her journal is a line the SOURCE says. The purpose, the verse
// and the conjuration are quoted, never summarised, and the only words this house adds are the
// headings that say which is which. `scripts/file-cabinet.mjs` holds that as containment — each
// source string must appear in the body BYTE FOR BYTE — which is the one shape a paraphrase cannot
// satisfy and a re-wording cannot pass by accident.
export function cabinetJournalEntry(item) {
  if (!item) return null;
  const lines = [];
  const push = (heading, value) => {
    const v = String(value === null || value === undefined ? "" : value).trim();
    if (v) lines.push(`${heading}\n${v}`);
  };
  const where = [];
  if (item.planet && item.ordinal) where.push(`${titleCase(item.planet)}, pentacle ${item.ordinal}`);
  if (item.figure) where.push(`Fig. ${item.figure}`);
  const drawer = cabinetDrawer(item.drawer);
  if (drawer) where.push(drawer.label);
  if (where.length) lines.push(where.join(" · "));
  push("What this seal is for", item.purpose);
  if (item.verseText) {
    push(item.verseRef ? `The verse (${item.verseRef})` : "The verse", item.verseText);
  }
  if (Array.isArray(item.waysToUse) && item.waysToUse.length) {
    push("Ways to use", item.waysToUse.join("\n"));
  }
  if (item.sealOfGod && item.sealOfGod.name) {
    push("The Seal of God it goes with", [item.sealOfGod.name, item.sealOfGod.use].filter(Boolean).join("\n"));
  }
  return { title: String(item.name || "A seal"), body: lines.join("\n\n") };
}

function titleCase(word) {
  const w = String(word || "");
  return w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : w;
}

// ─────────────────────────────────────────────────────────────────────────────
// WHAT RIDES TO THE CAULDRON.
//
// **THE SHAPE IS THE ONE THE POT ALREADY READS, NOT A NEW ONE.** `buildCauldronWorking` parses a
// gathered item into a piece by looking for `data.seal` (slug -> the pentacle's image, and the index
// is re-read server-side for its verse and its Seal of God pairing) and `data.sections[0].body` for
// what the working is about. A Sixth Book seal carries `slug: null` and its own plate URL, which is
// byte-for-byte what a real Moses working already sends — so nothing in the Cauldron changed for
// this round, and that is the argument for this shape rather than a tidier one.
//
// The KINDS are `CAULDRON_KINDS`' own, and the fixture holds the crossing (this module imports
// nothing, so it cannot check itself — the gate imports both and asserts every kind below is on the
// roster the route admits).
export function cabinetCauldronSend(item) {
  if (!cabinetCan(item, "cauldron")) return null;
  const kind = item.drawer === DRAWER_MOSES_SIXTH ? "moses-working" : "keys-working";
  const seal = {
    slug: item.slug || null,
    name: item.name,
    verseText: item.verseText || "",
    verseRef: item.verseRef || "",
    imageUrl: item.imageUrl,
  };
  if (item.figure) seal.figure = item.figure;
  return {
    kind,
    title: String(item.name || "A seal"),
    // Her verbatim purpose is what the pot reads to know what this piece is about. Quoted, never
    // summarised, for the same reason the journal entry is.
    data: { seal, sections: item.purpose ? [{ heading: "What this seal is for", body: item.purpose }] : [] },
  };
}

// The filename a download saves under. The bytes are the house's own plate at its native size and
// nothing is resampled — an enlarged seal is invented detail, which is the picture half of a false
// receipt (R141) — so what this promises is the plate whole, not a resolution it does not have.
export function cabinetDownloadName(item) {
  if (!item) return "seal.webp";
  const base = String(item.slug || item.name || "seal")
    .toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  return `${base || "seal"}.webp`;
}

// ─────────────────────────────────────────────────────────────────────────────
// FOUR STATES, NEVER TWO (R18/R64's cauldron ruling, one room over).
//
// A read that has not answered HOLDS STILL. Telling a member the Greater Keys drawer is empty
// because the database did not answer is the one wrong thing to say about a cabinet that has held
// forty-two seals since the day the room opened, and it is indistinguishable on screen from the
// truth. `items === null` is R39/W5-2's third state and it is why the reader takes it separately.
export function cabinetDrawerState({ loading = false, failed = false, items = null } = {}) {
  if (failed) return "failed";
  if (loading || items === null || items === undefined) return "waiting";
  if (!Array.isArray(items) || items.length === 0) return "empty";
  return "list";
}
